import { useState } from "react";

const isFolder = (n) => Array.isArray(n.item);

export function indexTree(nodes, parentPath = [], parentId = "") {
  return nodes.map((n, i) => {
    const id = parentId === "" ? String(i) : `${parentId}.${i}`;
    const path = [...parentPath, n.name];
    return isFolder(n)
      ? { ...n, id, path, kind: "folder", item: indexTree(n.item, path, id) }
      : { ...n, id, path, kind: "endpoint" };
  });
}

export function findNode(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.kind === "folder") {
      const hit = findNode(n.item, id);
      if (hit) return hit;
    }
  }
  return null;
}

export function collectEndpoints(nodes, out = []) {
  nodes.forEach((n) =>
    n.kind === "folder" ? collectEndpoints(n.item, out) : out.push(n)
  );
  return out;
}

export const countFolders = (nodes) =>
  nodes.reduce(
    (sum, n) => (n.kind === "folder" ? sum + 1 + countFolders(n.item) : sum),
    0
  );

export const methodOf = (n) => (n.request?.method || "GET").toUpperCase();
export const urlOf = (n) => n.request?.url?.raw || "";

export function matches(n, q) {
  return (
    n.name.toLowerCase().includes(q) ||
    (n.kind === "endpoint" &&
      (urlOf(n).toLowerCase().includes(q) || methodOf(n).toLowerCase().includes(q)))
  );
}

/** Keeps endpoints that match; keeps folders that match or contain a match. */
export function filterTree(nodes, q) {
  if (!q) return nodes;
  return nodes.reduce((acc, n) => {
    if (n.kind === "endpoint") {
      if (matches(n, q)) acc.push(n);
    } else if (n.name.toLowerCase().includes(q)) {
      acc.push(n);
    } else {
      const kids = filterTree(n.item, q);
      if (kids.length) acc.push({ ...n, item: kids });
    }
    return acc;
  }, []);
}

export function parseBody(body) {
  const raw = body?.raw?.trim();
  if (!raw) return null;
  try {
    return { ok: true, value: JSON.parse(raw), raw };
  } catch {
    return { ok: false, raw };
  }
}

/** Flattens a JSON value into rows: { path, type, example } */
export function flattenFields(value, prefix = "", rows = []) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    Object.entries(value).forEach(([k, v]) => {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        rows.push({ path, type: "object", example: "" });
        flattenFields(v, path, rows);
      } else if (Array.isArray(v)) {
        rows.push({ path, type: "array", example: JSON.stringify(v) });
      } else {
        rows.push({
          path,
          type: v === null ? "null" : typeof v,
          example: String(v).trim(),
        });
      }
    });
  }
  return rows;
}

export function findVariables(node) {
  const text = JSON.stringify(node.request || {});
  return [...new Set((text.match(/\{\{[^}]+\}\}/g) || []))];
}

export function highlightJson(json) {
  const esc = json.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  return esc.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (m) => {
      let c = "num";
      if (m.startsWith('"')) c = m.endsWith(":") ? "key" : "str";
      else if (m === "true" || m === "false") c = "bool";
      else if (m === "null") c = "nul";
      return `<span class="ad-t-${c}">${m}</span>`;
    }
  );
}

export function buildCurl(node) {
  const req = node.request;
  const parts = [`curl -X ${methodOf(node)} '${urlOf(node)}'`];
  (req.header || []).forEach((h) =>
    parts.push(`  -H '${h.key}: ${String(h.value).trim()}'`)
  );
  const body = parseBody(req.body);
  if (body) {
    const compact = body.ok ? JSON.stringify(body.value) : body.raw;
    parts.push(`  -d '${compact}'`);
  }
  return parts.join(" \\\n");
}

export function buildFetch(node) {
  const req = node.request;
  const headers = Object.fromEntries(
    (req.header || []).map((h) => [h.key, String(h.value).trim()])
  );
  const body = parseBody(req.body);
  const lines = [
    `const res = await fetch("${urlOf(node)}", {`,
    `  method: "${methodOf(node)}",`,
    `  headers: ${JSON.stringify(headers, null, 2).replace(/\n/g, "\n  ")},`,
  ];
  if (body) {
    lines.push(
      body.ok
        ? `  body: JSON.stringify(${JSON.stringify(body.value, null, 2).replace(/\n/g, "\n  ")}),`
        : `  body: ${JSON.stringify(body.raw)},`
    );
  }
  lines.push("});", "const data = await res.json();");
  return lines.join("\n");
}

export function MethodTag({ method, small }) {
  const label = small && method === "DELETE" ? "DEL" : method;
  return (
    <span className={`ad-method ad-m-${method.toLowerCase()} ${small ? "is-small" : ""}`}>
      {label}
    </span>
  );
}

export function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button type="button" className="ad-copy" onClick={copy}>
      {done ? "Copied" : label}
    </button>
  );
}

export function VarText({ text }) {
  const parts = String(text).trim().split(/(\{\{[^}]+\}\})/g);
  return (
    <>
      {parts.map((p, i) =>
        /^\{\{.*\}\}$/.test(p) ? (
          <span key={i} className="ad-var">{p}</span>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

export function CodeBlock({ title, code, json }) {
  return (
    <div className="ad-code">
      <div className="ad-code-bar">
        <span>{title}</span>
        <CopyButton text={code} />
      </div>
      {json ? (
        <pre dangerouslySetInnerHTML={{ __html: highlightJson(code) }} />
      ) : (
        <pre>{code}</pre>
      )}
    </div>
  );
}

export function Section({ title, hint, children }) {
  return (
    <section className="ad-section">
      <h3>
        {title}
        {hint && <span className="ad-hint">{hint}</span>}
      </h3>
      {children}
    </section>
  );
}



export function Breadcrumb({ path, onSelect, tree }) {
  if (!path.length) return null;
  return (
    <div className="ad-crumbs">
      {path.slice(0, -1).map((p, i) => {
        // resolve the folder id for each ancestor
        let nodes = tree;
        let id = "";
        for (let d = 0; d <= i; d++) {
          const hit = nodes.find((n) => n.name === path[d] && n.kind === "folder");
          if (!hit) return <span key={i}>{p}</span>;
          id = hit.id;
          nodes = hit.item;
        }
        return (
          <button key={i} type="button" onClick={() => onSelect(id)}>
            {p}
          </button>
        );
      })}
    </div>
  );
}


export function readHash() {
  return decodeURIComponent(window.location.hash.replace(/^#\/?/, ""));
}